import React, { useState } from "react";
import { Star } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchRating,
  ratingQueryKey,
  removeRating,
  submitRating,
  type RatingItemType,
  type RatingResponse,
  type RatingSummary,
} from "../api/ratings";

// One-click star rating for a collected peak / trail / landmark.
//
// State lives in the ["ratings", type, id] query so the detail page and the
// map inspector always agree. Detail pages pass the summary embedded in
// their response as `initial` (no extra request); the map inspector omits it
// and the component fetches on mount.
//
// Rendering rules:
//   - canRate (you have collected it): the stars are YOUR rating - empty
//     until you click one, gold once set, hover previews. Clicking your
//     current star again removes the rating.
//   - otherwise: the stars show the community average, read-only, muted.
//   - the caption always carries the average + how many explorers, and your
//     own rating when you have one.

const CSS = `
.atlas-stars{ display:inline-flex; align-items:center; gap:2px; }
.atlas-stars button{
  appearance:none; background:none; border:0; padding:2px; margin:0; cursor:pointer;
  display:grid; place-items:center; color:var(--text-faint); border-radius:var(--radius-xs);
  transition:transform .12s ease, color .12s ease;
}
.atlas-stars button:hover{ transform:scale(1.15); }
.atlas-stars button:focus-visible{ outline:2px solid var(--border-gold); outline-offset:1px; }
.atlas-stars button:disabled{ cursor:default; transform:none; }
.atlas-stars .lit{ color:var(--gold-400); }
.atlas-stars .lit-muted{ color:var(--text-muted); }
.atlas-stars .preview{ color:var(--gold-300); }
`;

function ensure() {
  if (typeof document === "undefined") return;
  if (!document.getElementById("atlas-stars-css")) {
    const s = document.createElement("style");
    s.id = "atlas-stars-css";
    s.textContent = CSS;
    document.head.appendChild(s);
  }
}

interface StarRatingProps {
  itemType: RatingItemType;
  itemId: string;
  /** Summary embedded in a detail response - seeds the cache, skips a fetch. */
  initial?: RatingSummary;
  /** Compact layout for the map inspector. */
  compact?: boolean;
  /** Logged-out viewers get the read-only average and no hint. */
  isAuthenticated?: boolean;
  /**
   * Demo mode (landing page map): never talks to the API. Shows `initial`
   * and keeps any rating the visitor clicks in the local cache only.
   */
  static?: boolean;
}

export function ratingCaption(r: RatingSummary): string {
  if (r.count === 0) return "No ratings yet";
  return `${r.average?.toFixed(1)} from ${r.count} ${r.count === 1 ? "explorer" : "explorers"}`;
}

export function StarRating({ itemType, itemId, initial, compact, isAuthenticated = true, static: isStatic = false }: StarRatingProps) {
  ensure();
  const qc = useQueryClient();
  const key = ratingQueryKey(itemType, itemId);
  const [hover, setHover] = useState<number | null>(null);

  const query = useQuery({
    queryKey: key,
    queryFn: () => fetchRating(itemType, itemId),
    initialData: initial ? { rating: initial } : undefined,
    staleTime: 30_000,
    enabled: !isStatic,
  });
  const summary = query.data?.rating ?? null;

  const mutation = useMutation({
    mutationFn: (value: number | null) => {
      if (isStatic) {
        const prev = qc.getQueryData<RatingResponse>(key);
        return Promise.resolve<RatingResponse>({
          rating: { ...(prev?.rating ?? { average: null, count: 0, canRate: true }), mine: value },
        });
      }
      return value == null ? removeRating(itemType, itemId) : submitRating(itemType, itemId, value);
    },
    // Optimistic: swap "mine" in place so the click lands instantly; the
    // server response then brings the recomputed average + count.
    onMutate: async (value) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<RatingResponse>(key);
      if (prev) qc.setQueryData<RatingResponse>(key, { rating: { ...prev.rating, mine: value } });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    onSuccess: (data) => {
      qc.setQueryData(key, data);
    },
  });

  if (!summary) {
    // Loading (map inspector path) - hold the space so the panel doesn't jump.
    return <div style={{ height: compact ? 20 : 26 }} aria-hidden />;
  }

  const canRate = isAuthenticated && summary.canRate;
  const mine = summary.mine;
  const size = compact ? 16 : 22;

  // What the five stars depict: your rating when you can rate, else the
  // community average (rounded to whole stars for a static display).
  const shown = canRate ? (hover ?? mine ?? 0) : Math.round(summary.average ?? 0);
  const litClass = canRate ? (hover != null ? "preview" : "lit") : "lit-muted";

  const stars = (
    <div
      className="atlas-stars"
      role={canRate ? "radiogroup" : "img"}
      aria-label={
        canRate
          ? mine != null
            ? `Your rating: ${mine} out of 5`
            : "Rate this out of 5"
          : summary.count > 0
            ? `Average rating ${summary.average} out of 5`
            : "No ratings yet"
      }
      onMouseLeave={() => setHover(null)}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const lit = n <= shown;
        return (
          <button
            key={n}
            type="button"
            className={lit ? litClass : undefined}
            disabled={!canRate || mutation.isPending}
            role={canRate ? "radio" : undefined}
            aria-checked={canRate ? mine === n : undefined}
            aria-label={canRate ? `${n} star${n === 1 ? "" : "s"}` : undefined}
            title={
              canRate
                ? mine === n
                  ? "Click again to remove your rating"
                  : `Rate ${n} out of 5`
                : undefined
            }
            onMouseEnter={() => canRate && setHover(n)}
            onFocus={() => canRate && setHover(n)}
            onBlur={() => setHover(null)}
            onClick={() => {
              if (!canRate) return;
              setHover(null);
              mutation.mutate(mine === n ? null : n);
            }}
          >
            <Star size={size} strokeWidth={1.75} fill={lit ? "currentColor" : "none"} />
          </button>
        );
      })}
    </div>
  );

  const caption = ratingCaption(summary);
  const hint =
    isAuthenticated && !summary.canRate
      ? itemType === "trail"
        ? "Finish the trail to rate it"
        : "Collect it to rate it"
      : canRate && mine == null && summary.count === 0
        ? "Be the first"
        : null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: compact ? "row" : "column",
        alignItems: compact ? "center" : "flex-start",
        gap: compact ? 10 : 6,
        flexWrap: "wrap",
      }}
    >
      {stars}
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 8,
          flexWrap: "wrap",
          fontSize: compact ? 12 : 13,
          color: "var(--text-secondary)",
          lineHeight: 1.3,
        }}
      >
        <span>
          {mine != null && (
            <>
              <span style={{ color: "var(--text-primary)", fontWeight: 600 }}>You rated {mine}</span>
              <span style={{ color: "var(--text-faint)" }}> · </span>
            </>
          )}
          {summary.count > 0 ? (
            <>
              <span style={{ fontFamily: "var(--font-mono)", fontWeight: 600, color: "var(--text-primary)" }}>
                {summary.average?.toFixed(1)}
              </span>{" "}
              <span style={{ color: "var(--text-muted)" }}>
                {caption.replace(/^\S+\s/, "")}
              </span>
            </>
          ) : (
            <span style={{ color: "var(--text-muted)" }}>{caption}</span>
          )}
        </span>
        {hint && <span style={{ fontSize: compact ? 11 : 12, color: "var(--text-faint)" }}>{hint}</span>}
        {mutation.isError && (
          <span style={{ fontSize: compact ? 11 : 12, color: "var(--coral-300)" }}>
            Couldn't save your rating
          </span>
        )}
      </div>
    </div>
  );
}
